import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from '../../firebase_config';
import {
  LINKPRICE_REWARDS_COLLECTION,
  LINKPRICE_CANCELS_COLLECTION,
  LINKPRICE_TRANSACTIONS_COLLECTION,
  COUPANG_CANCELS_COLLECTION,
  USER_COLLECTION,
  ENTERPRISE_COLLECTION,
  DailyChartData,
} from '../../type';

const COUPANG_ORDERS = process.env.REACT_APP_COUPANG_ORDERS_COLLECTION as string;
const LINKPRICE_REWARDS = LINKPRICE_REWARDS_COLLECTION;
const COUPANG_CANCELS = COUPANG_CANCELS_COLLECTION;
const LINKPRICE_CANCELS = LINKPRICE_CANCELS_COLLECTION;
const LINKPRICE_TRANSACTIONS = LINKPRICE_TRANSACTIONS_COLLECTION;
const LINKPRICE_CONFIG = 'linkprice_reconciliation_config';

function isValid(name: any): name is string {
  return typeof name === 'string' && name.length > 0;
}

async function safeFetchAll(collectionName: string | undefined): Promise<Record<string, any>[]> {
  if (!isValid(collectionName)) return [];
  try {
    const snap = await getDocs(collection(db, collectionName));
    return snap.docs.map((doc) => ({ _id: doc.id, ...doc.data() }));
  } catch (e) {
    console.warn(`[Dashboard] Failed to fetch ${collectionName}:`, e);
    return [];
  }
}

async function fetchAll(collectionName: string): Promise<Record<string, any>[]> {
  if (!isValid(collectionName)) {
    throw new Error('Collection name is not configured');
  }
  const snap = await getDocs(collection(db, collectionName));
  return snap.docs.map((document) => ({ _id: document.id, ...document.data() }));
}

async function fetchLinkpriceData(): Promise<{
  rewards: Record<string, any>[];
  cancels: Record<string, any>[];
}> {
  try {
    const config = await getDoc(doc(db, LINKPRICE_CONFIG, 'current'));
    if (config.data()?.readMode === 'reconciled') {
      const transactions = await fetchAll(LINKPRICE_TRANSACTIONS);
      return {
        rewards: transactions.filter((transaction) => transaction.apiSeen === true),
        cancels: transactions.filter((transaction) =>
          transaction.settlementStatus === 'cancel_pending' ||
          transaction.settlementStatus === 'cancelled'
        ),
      };
    }
  } catch (error) {
    console.warn('[Dashboard] Falling back to legacy Linkprice data:', error);
  }

  const [rewards, cancels] = await Promise.all([
    safeFetchAll(LINKPRICE_REWARDS),
    safeFetchAll(LINKPRICE_CANCELS),
  ]);
  return { rewards, cancels };
}

async function safeCount(collectionName: string | undefined): Promise<number> {
  if (!isValid(collectionName)) return 0;
  try {
    const snap = await getCountFromServer(collection(db, collectionName));
    return snap.data().count;
  } catch {
    return 0;
  }
}

// YYYYMMDD → YYYY-MM-DD
function normDate(raw: string): string {
  if (!raw) return '';
  const s = String(raw);
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return '';
}

function num(doc: Record<string, any>, ...keys: string[]): number {
  for (const k of keys) {
    const v = doc[k];
    if (v !== undefined && v !== null) return Number(v) || 0;
  }
  return 0;
}

function getEnterpriseId(doc: Record<string, any>): string | null {
  const eid = doc.enterpriseId;
  if (typeof eid === 'string' && eid.length > 0) return eid;
  return null;
}

export interface RawDashboardData {
  coupangOrders: Record<string, any>[];
  coupangCancels: Record<string, any>[];
  linkpriceRewards: Record<string, any>[];
  linkpriceCancels: Record<string, any>[];
  totalUsers: number;
  totalEnterprises: number;
  enterprises: { id: string; name: string }[];
}

// 개별 문서 → 기업 필터 → 일별 집계
export function aggregateDailyData(
  raw: RawDashboardData,
  enterpriseFilter: string | null // null = 전체
): DailyChartData[] {
  const matchEnterprise = (doc: Record<string, any>) => {
    if (!enterpriseFilter) return true;
    return getEnterpriseId(doc) === enterpriseFilter;
  };

  // ── 쿠팡 주문 집계 ──
  const coupangByDate = new Map<string, { orders: number; revenue: number }>();
  for (const doc of raw.coupangOrders) {
    if (!matchEnterprise(doc)) continue;
    const date = normDate(doc.date || doc._id);
    if (!date) continue;
    const prev = coupangByDate.get(date) || { orders: 0, revenue: 0 };
    prev.orders += 1;
    prev.revenue += num(doc, 'commission') / 2;
    coupangByDate.set(date, prev);
  }

  // ── 쿠팡 취소 집계 ──
  const coupangCancelsByDate = new Map<string, { cancels: number; amount: number }>();
  for (const doc of raw.coupangCancels) {
    if (!matchEnterprise(doc)) continue;
    const date = normDate(doc.date || doc.orderDate || doc._id);
    if (!date) continue;
    const prev = coupangCancelsByDate.get(date) || { cancels: 0, amount: 0 };
    prev.cancels += 1;
    prev.amount += Math.abs(num(doc, 'commission')) / 2;
    coupangCancelsByDate.set(date, prev);
  }

  // ── 링크프라이스 리워드 집계 ──
  const linkpriceByDate = new Map<string, { count: number; revenue: number }>();
  for (const doc of raw.linkpriceRewards) {
    if (!matchEnterprise(doc)) continue;
    const date = normDate(doc.orderedDate || doc.day || doc.date || doc._id);
    if (!date) continue;
    const prev = linkpriceByDate.get(date) || { count: 0, revenue: 0 };
    prev.count += 1;
    prev.revenue += num(doc, 'commision', 'commission') / 2;
    linkpriceByDate.set(date, prev);
  }

  // ── 링크프라이스 취소 집계 ──
  const linkpriceCancelsByDate = new Map<string, { cancels: number; amount: number }>();
  for (const doc of raw.linkpriceCancels) {
    if (!matchEnterprise(doc)) continue;
    const date = normDate(doc.orderedDate || doc.yyyymmdd || doc.date || doc._id);
    if (!date) continue;
    const prev = linkpriceCancelsByDate.get(date) || { cancels: 0, amount: 0 };
    prev.cancels += 1;
    prev.amount += Math.abs(num(doc, 'commission')) / 2;
    linkpriceCancelsByDate.set(date, prev);
  }

  // ── 날짜 병합 ──
  const dateSet = new Set<string>();
  coupangByDate.forEach((_, d) => dateSet.add(d));
  coupangCancelsByDate.forEach((_, d) => dateSet.add(d));
  linkpriceByDate.forEach((_, d) => dateSet.add(d));
  linkpriceCancelsByDate.forEach((_, d) => dateSet.add(d));

  return Array.from(dateSet)
    .sort()
    .map((date) => {
      const cs = coupangByDate.get(date);
      const cc = coupangCancelsByDate.get(date);
      const lp = linkpriceByDate.get(date);
      const lc = linkpriceCancelsByDate.get(date);
      return {
        date,
        coupangRevenue: cs?.revenue || 0,
        coupangOrders: cs?.orders || 0,
        coupangCommission: 0,
        linkpriceRevenue: lp?.revenue || 0,
        linkpriceRewards: lp?.count || 0,
        linkpriceCommission: 0,
        coupangCancels: cc?.cancels || 0,
        coupangCancelAmount: cc?.amount || 0,
        linkpriceCancels: lc?.cancels || 0,
        linkpriceCancelAmount: lc?.amount || 0,
      };
    });
}

class DashboardService {
  async fetchRawData(): Promise<RawDashboardData> {
    const [
      coupangOrders,
      coupangCancels,
      linkpriceData,
      totalUsers,
      totalEnterprises,
      enterpriseDocs,
    ] = await Promise.all([
      safeFetchAll(COUPANG_ORDERS),
      safeFetchAll(COUPANG_CANCELS),
      fetchLinkpriceData(),
      safeCount(USER_COLLECTION),
      safeCount(ENTERPRISE_COLLECTION),
      safeFetchAll(ENTERPRISE_COLLECTION),
    ]);

    const enterprises = enterpriseDocs.map((doc) => ({
      id: doc.enterpriseId || doc._id,
      name: doc.enterpriseName || doc._id,
    }));

    return {
      coupangOrders,
      coupangCancels,
      linkpriceRewards: linkpriceData.rewards,
      linkpriceCancels: linkpriceData.cancels,
      totalUsers,
      totalEnterprises,
      enterprises,
    };
  }
}

export const dashboardService = new DashboardService();
